const timeout = time => new Promise(resolve => setTimeout(resolve, time));

const gameShell = {
  init({
    assets, managers, screens, game, config,
  }) {
    const canvas = document.querySelector('#game-canvas');

    this.stage = new createjs.Stage(canvas);
    this.stage.preventSelection = false;

    this.managers = managers;
    this.managers.server = managers.realServer;
    this.config = config;
    this.game = game;
    this.autoGame = false;
    this.needUpdate = false;

    this.popups = [
      this.managers.rules,
      !this.config.desktop && this.managers.bet,
    ].filter(Boolean);

    this.data = {
      win: 0,
      balance: 0,
      doubleStep: 0,
    };

    Promise.all([
      managers.assets.init(assets),
      managers.logger.init(config.server_url),
      managers.server.init(config)
    ]).then(([,,res]) => {
      this.setBetData(res);

      if (config.desktop) {
        managers.gui.setBet(this.betData);
      } else {
        managers.hud.init(this.betData);
        managers.bet.init(this.betData, config);
      }
      managers.rules.init(config, assets.lib, this.betData);

      game.init(this.stage, screens, config, assets.lib);
      game.showLines(this.betData.lines);

      this.handleServerResponse(res);
      this.needUpdate = true;
    }).catch((err) => {
      if (!managers.alert.isVisible()) {
        managers.alert.show('error');
      }

      throw err;
    });

    if (this.config.desktop) {
      // eslint-disable-next-line
      managers.bet = managers.hud = managers.gui = managers.guiDesktop;
    }
    managers.gui.init(config);
    managers.resize.init(canvas, assets.lib, config.desktop);
    managers.wincomb.init(config);
    managers.alert.init();

    this.bindEvents();
    this.setTicker(assets.lib);
  },

  play() {
    this.managers.gui.setState('disabled');
    this.managers.server.play(this.betData)
      .then((res) => {
        console.log(res);

        this.data.win = res.win;
        this.setBalance(res.balance);

        const winSymbols = this.managers.wincomb.getWinSymbols(res.symbols, this.betData.lines);
        this.game.play(res, winSymbols).then(() => this.updateAfterPlay(res));
      });
  },

  updateAfterPlay(res) {
    const takeWin = () => this.managers.server.finish()
      .then((balance) => {
        this.setBalance(balance);
        this.validateBet();
      });

    if (res.freeSpins !== res.freeSpinsDone) {
      if (res.freeSpinsDone === 0 && this.autoGame) {
        this.setAutoGame(false);
        this.managers.gui.setState('free-ready');
      } else {
        this.setAutoGame(false);
        timeout(res.spinWin ? 1100 : 150).then(() => this.play());
      }
      return;
    }

    if (this.autoGame) {
      timeout(res.spinWin ? 500 : 75)
        .then(() => (res.spinWin ? takeWin() : this.validateBet()))
        .then(() => this.autoGame && this.managers.gui.setState('disabled'))
        .then(() => this.autoGame && timeout(res.spinWin ? 750 : 75))
        .then(() => (this.autoGame ? this.play() : this.validateBet()));
      return;
    }

    if (res.win) {
      if (this.config.double) {
        this.managers.gui.setDouble(true);
        this.managers.gui.setDoubleStep(0);
        this.game.showDouble(this.data.doubleStep);
        this.validateBet();
      } else {
        takeWin();
      }
    } else if (res.freeSpins !== 0 && res.freeSpins === res.freeSpinsDone) {
      takeWin();
    } else {
      this.validateBet();
    }
  },

  playDouble() {
    this.managers.gui.setState('disabled');
    this.managers.server.playDouble()
      .then(({ win }) => {
        const step = this.data.doubleStep;

        this.data.doubleStep += 1;
        this.data.win = win;
        return this.game.playDouble(win, step);
      })
      .then(() => {
        if (this.data.win && this.data.doubleStep < 5) {
          this.game.showDouble(this.data.doubleStep);
          this.managers.gui.setState('ready');
          this.managers.gui.setDoubleStep(this.data.doubleStep);
        } else {
          this.endDouble();
        }
      });
  },

  changeBet(changeBet, changeLines, maxbet = false) {
    let newBetIndex = this.betData.betIndex + changeBet;
    let newLinesIndex = this.betData.linesIndex + changeLines;

    if (maxbet) {
      [newBetIndex, newLinesIndex] = this.getMaxBet();
    }

    const lines = this.config.lines[newLinesIndex];
    const bet = this.config.bets[newBetIndex];

    if (!lines || !bet) {
      return;
    }

    this.setBetData({ bet, lines });

    this.managers.bet.setBet(this.betData);
    this.managers.hud.setBet(this.betData);
    this.managers.rules.setBet(this.betData);
    this.game.showLines(lines);
    this.needUpdate = true;

    this.validateBet();
  },

  getMaxBet() {
    const { bets, lines } = this.config;

    for (let i = lines.length - 1; i >= 0; i -= 1) {
      for (let j = bets.length - 1; j >= 0; j -= 1) {
        const currentBet = bets[j] * lines[i];
        if (currentBet <= this.data.balance) {
          return [j, i];
        }
      }
    }

    return [0, 0];
  },

  validateBet() {
    const valid = this.data.win || this.data.balance >= this.betData.currentBet;

    this.managers.hud.setValidState(valid);
    this.managers.bet.setValidState(valid);

    if (!valid) {
      this.setAutoGame(false);
    }

    if (!this.managers.bet.isVisible()) {
      this.managers.gui.setState(valid ? 'ready' : 'not-ready');
    }

    if (!valid && !this.config.desktop) {
      setTimeout(() => this.handleInvalidBet(), 1000);
    }
  },

  handleInvalidBet() {
    if (
      this.data.balance >= this.betData.currentBet ||
      this.managers.bet.isVisible()
    ) {
      return;
    }

    this.togglePopup('bet');
  },

  togglePopup(name) {
    const popup = this.managers[name];
    const wasVisible = popup.isVisible();

    this.popups.forEach(p => p.hide());
    if (wasVisible) {
      this.validateBet();
    } else {
      popup.show();
      this.managers.gui.setState(name);
    }
  },

  endDouble() {
    this.managers.gui.setState('disabled');
    this.managers.server.finish()
      .then((balance) => {
        this.setBalance(balance);
        this.data.doubleStep = 0;
        this.data.win = 0;
        this.managers.gui.setDouble(false);
        this.validateBet();
      });
  },

  closeDouble() {
    this.endDouble();
    this.game.stop();
  },

  setBalance(balance) {
    this.data.balance = balance;
    this.managers.hud.setBalance(balance);
  },

  handleServerResponse({ balance, freeSpins, freeSpinsDone }) {
    this.setBalance(balance);

    if (freeSpins !== freeSpinsDone) {
      this.game.showFreeSpinsLeft(freeSpins, freeSpinsDone)
        .then(() => this.play());
    } else {
      this.validateBet();
    }
  },

  setBetData(res) {
    const betIndex = res.bet ? this.config.bets.indexOf(res.bet) : 0;
    const linesIndex = res.lines
      ? this.config.lines.indexOf(res.lines)
      : this.config.lines.length - 1;

    const bet = this.config.bets[betIndex];
    const lines = this.config.lines[linesIndex];
    const currentBet = bet * lines;

    this.betData = {
      betIndex,
      linesIndex,
      bet,
      lines,
      currentBet,
      firstBet: betIndex === 0,
      lastBet: betIndex === this.config.bets.length - 1,
      firstLine: linesIndex === 0,
      lastLine: linesIndex === this.config.lines.length - 1,
    };
  },

  setAutoGame(auto) {
    this.autoGame = auto;
    this.managers.gui.setAutoGame(auto);

    if (auto) {
      this.play();
    }
  },

  bindEvents() {
    this.game.addEventListener('play-double', () => this.playDouble());

    this.managers.rules.addEventListener('close', () => this.togglePopup('rules'));

    this.managers.bet.addEventListener('close', () => this.togglePopup('bet'));
    this.managers.bet.addEventListener('bet-up', () => this.changeBet(1, 0));
    this.managers.bet.addEventListener('bet-down', () => this.changeBet(-1, 0));
    this.managers.bet.addEventListener('lines-up', () => this.changeBet(0, 1));
    this.managers.bet.addEventListener('lines-down', () => this.changeBet(0, -1));
    this.managers.bet.addEventListener('bet-max', () => this.changeBet(0, 0, true));

    this.managers.gui.addEventListener('play', () => this.play());
    this.managers.gui.addEventListener('play-double', () => this.playDouble());
    this.managers.gui.addEventListener('close-double', () => this.closeDouble());
    this.managers.gui.addEventListener('toggle-auto-game', () => this.setAutoGame(!this.autoGame));
    this.managers.gui.addEventListener('toggle-rules', () => this.togglePopup('rules'));
    this.managers.gui.addEventListener('toggle-bet', () => {
      this.game.showLines(this.betData.lines);
      this.needUpdate = true;
      this.togglePopup('bet');
    });
    this.managers.gui.addEventListener('close-game', () => {
      window.parent.location = document.referrer;
    });
    this.managers.gui.addEventListener('close-demo', () => {
      window.parent.location = document.referrer + 'game/' + this.config.gameId;
    });
    this.managers.gui.addEventListener('refill', () => {
      window.parent.location.href = document.referrer + this.config.refill_url;
    });

    this.managers.server.addEventListener('error', () => this.managers.alert.show('error'));
    this.managers.server.addEventListener('no-auth', () => window.location.replace(this.config.login_url));
  },

  setTicker(lib) {
    createjs.Ticker.timingMode = createjs.Ticker.RAF_SYNCHED;
    createjs.Ticker.framerate = lib.properties.fps;
    createjs.Ticker.addEventListener('tick', e => this.tick(e));
  },

  tick(e) {
    this.managers.resize.tick();

    if (e.paused) {
      return;
    }

    const hasTweens = createjs.Tween.hasActiveTweens();
    if (hasTweens || this.needUpdate) {
      this.game.tick(e);
      this.stage.update(e);
    }
    // для обновления после последнего кадра
    this.needUpdate = hasTweens;
  },
};

export default gameShell;



// WEBPACK FOOTER //
// ../base/js/game-shell.js